import { Box, Button, Divider, Modal, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import IconButton from '../ui/IconButton';

export interface ModalProps {
    open: boolean;
    handleClose: () => void;
}

interface ModalBodyProps extends ModalProps {
    title?: string;
    subtitle?: string;
    description?: string;
    onSubmit?: () => void;
    onSecondaryAction?: () => void;
    submitText?: string;
    showSecondaryButton?: boolean;
    secondaryButtonText?: string;
    children?: React.ReactNode;
    disableSubmit?: boolean;
    disableSecondaryButton?: boolean;
    height?: string | number;
    width?: string | number;
    closeOnSubmit?: boolean;
    showSubmitButton?: boolean;
    loading?: boolean;
    submitLoading?: boolean;
    secondaryLoading?: boolean;
}

const ModalBody: React.FC<ModalBodyProps> = ({
    title,
    subtitle,
    description,
    open,
    handleClose,
    onSubmit = () => {},
    onSecondaryAction = () => {},
    submitText,
    children,
    showSecondaryButton = false,
    secondaryButtonText = '',
    disableSubmit = false,
    disableSecondaryButton = false,
    showSubmitButton = true,
    height = 'auto',
    width = '500px',
    closeOnSubmit = true,
    loading = false,
    submitLoading = false,
    secondaryLoading = false,
}) => {
    const { t } = useTranslation();
    const { muiTheme } = useTheme();

    return (
        <Modal open={open} onClose={handleClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    height: { xs: '100%', md: height },
                    width: { xs: '100%', md: width },
                    maxWidth: { xs: '100%', md: '80%' },
                    maxHeight: { xs: '100%', md: '80%' },
                    bgcolor: muiTheme.palette.background.paper,
                    borderRadius: { xs: 0, md: 2 },
                    boxShadow: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    outline: 'none',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        bgcolor: muiTheme.palette.common.black,
                        color: muiTheme.palette.common.white,
                    }}
                >
                    <Typography variant="h6">{capitalizeFirstLetter(title)}</Typography>
                    <IconButton iconName="close" onClick={handleClose} color="inherit" title={capitalizeFirstLetter(t('close'))} />
                </Box>

                {subtitle && (
                    <Box sx={{ bgcolor: 'grey.900', px: 2, py: 1 }}>
                        <Typography variant="subtitle1" color="white">
                            {capitalizeFirstLetter(subtitle)}
                        </Typography>
                    </Box>
                )}

                {description && (
                    <Box sx={{ bgcolor: 'grey. 800', px: 2, py: 1 }}>
                        <Typography variant="body2" color="white">
                            {description}
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        p: 2,
                        overflowY: 'auto',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    {children}
                </Box>

                {(showSubmitButton || showSecondaryButton) && <Divider />}

                {(showSubmitButton || showSecondaryButton) && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                            p: 2,
                        }}
                    >
                        {showSecondaryButton && (
                            <Button
                                variant="outlined"
                                color="error"
                                disabled={disableSecondaryButton}
                                onClick={() => {
                                    onSecondaryAction();
                                    handleClose();
                                }}
                                loading={secondaryLoading}
                            >
                                {secondaryButtonText}
                            </Button>
                        )}
                        {showSubmitButton && (
                            <Button
                                variant="contained"
                                color="success"
                                disabled={disableSubmit}
                                onClick={() => {
                                    onSubmit();
                                    if (closeOnSubmit) handleClose();
                                }}
                                loading={submitLoading}
                            >
                                {submitText || capitalizeFirstLetter(t('validate'))}
                            </Button>
                        )}
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default ModalBody;
